/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { MutableRef } from 'preact/hooks';
import { ColumnResizeInfo, TableColumn } from '../Table';
import { ColumnResizeDetail, ColumnWidths } from '../../UNSAFE_TableView';
import { getElementContainsFunc } from '../../utils/PRIVATE_collectionUtils';

/**
 * Helper function to handle a hover over a resizable column region gesture.
 */
export function handleOverResizeRegionGesture<K, D, C>(
  root: HTMLDivElement,
  tableId: string,
  event: PointerEvent,
  columnsArray: TableColumn<K, D, C>[],
  resizeInfoRef: MutableRef<ColumnResizeInfo<C>>,
  isRtl: boolean
) {
  // handle hover feedback around potentially resizable columns
  _updateResizingState(root, tableId, event, columnsArray, resizeInfoRef, isRtl);
}

/**
 * Helper function to handle a column resize start gesture (pointer down).
 */
export function handleColumnResizeStartGesture<K, D, C extends string>(
  root: HTMLDivElement,
  tableId: string,
  event: PointerEvent,
  columnsArray: TableColumn<K, D, C>[],
  appliedColumnWidths: ColumnWidths<C>,
  resizeInfoRef: MutableRef<ColumnResizeInfo<C>>,
  isRtl: boolean,
  fullColumnWidths?: number[],
  onColumnResizing?: (detail: ColumnResizeDetail<C>) => void
) {
  if (fullColumnWidths != null) {
    if (resizeInfoRef.current.key == null) {
      _updateResizingState(root, tableId, event, columnsArray, resizeInfoRef, isRtl);
    }
    if (resizeInfoRef.current.key != null) {
      const innerTable = root.querySelector(`[data-oj-table-inner-table='${tableId}']`);
      if (innerTable != null) {
        const rect = innerTable.getBoundingClientRect();
        const pointerX = isRtl ? rect.right - event.clientX : event.clientX - rect.left;

        const resizeColumnWidths = [];
        for (let i = 0; i < fullColumnWidths.length; i++) {
          const columnWidth = fullColumnWidths[i];
          resizeColumnWidths.push(columnWidth as number);
        }
        resizeInfoRef.current = {
          isResizing: true,
          isResizeHover: true,
          key: resizeInfoRef.current.key,
          startX: pointerX,
          delta: 0,
          fullColumnWidths: resizeColumnWidths,
          ignoreClick: resizeInfoRef.current.ignoreClick
        };
        if (onColumnResizing != null) {
          onColumnResizing({
            key: resizeInfoRef.current.key,
            delta: 0,
            allColumnWidths: appliedColumnWidths
          });
        }
        return true;
      }
    }
  }
  return false;
}

/**
 * Helper function to ensure the resizing state is updated based on the latest pointer event.
 */
function _updateResizingState<K, D, C>(
  root: HTMLDivElement,
  tableId: string,
  event: PointerEvent,
  columnsArray: TableColumn<K, D, C>[],
  resizeInfoRef: MutableRef<ColumnResizeInfo<C>>,
  isRtl: boolean
) {
  const headerRowElement = root.querySelector(
    `[data-oj-table-header-row='${tableId}']`
  ) as HTMLElement;
  if (headerRowElement != null) {
    const headerElements = headerRowElement.querySelectorAll(
      `[data-oj-table-header-cell='${tableId}']`
    ) as NodeListOf<HTMLElement>;
    if (getElementContainsFunc(headerRowElement)(event.target as HTMLElement)) {
      let isResizeHover = false;
      for (let i = 0; i < headerElements.length; i++) {
        const headerElement = headerElements[i];
        const cellRect = headerElement.getBoundingClientRect();
        const endEdge = isRtl ? cellRect.left : cellRect.right;
        if (Math.abs(endEdge - event.pageX) <= 8) {
          if (columnsArray[i].value.edgeResizable !== 'enabled') {
            break;
          }
          // resize operation on end side of header cell
          resizeInfoRef.current.key = columnsArray[i].key;
          headerRowElement.style.cursor = 'col-resize';
          isResizeHover = true;
          resizeInfoRef.current.isResizeHover = true;
        }
      }
      if (!isResizeHover) {
        headerRowElement.style.cursor = '';
        resizeInfoRef.current.key = undefined;
        resizeInfoRef.current.isResizeHover = false;
      }
    } else {
      if (headerRowElement != null) {
        headerRowElement.style.cursor = '';
      }
      resizeInfoRef.current.key = undefined;
      resizeInfoRef.current.isResizeHover = false;
    }
  }
}

/**
 * Helper function to handle a pointer move gesture during a column resize.
 */
export function handleColumnResizingGesture<C extends string>(
  root: HTMLDivElement,
  tableId: string,
  event: PointerEvent,
  appliedColumnWidths: ColumnWidths<C>,
  resizeInfoRef: MutableRef<ColumnResizeInfo<C>>,
  isRtl: boolean,
  fullColumnWidths?: number[],
  onColumnResizing?: (detail: ColumnResizeDetail<C>) => void
) {
  if (resizeInfoRef.current.isResizing && fullColumnWidths != null) {
    const innerTable = root.querySelector(`[data-oj-table-inner-table='${tableId}']`);
    if (innerTable != null) {
      const rect = innerTable.getBoundingClientRect();
      const currentX = isRtl ? rect.right - event.clientX : event.clientX - rect.left;

      resizeInfoRef.current.delta = currentX - resizeInfoRef.current.startX;
      // handle resize move operation
      if (onColumnResizing != null) {
        onColumnResizing({
          key: resizeInfoRef.current.key,
          delta: resizeInfoRef.current.delta,
          allColumnWidths: appliedColumnWidths
        });
      }
    }
  }
}

/**
 * Helper function to handle a column resize end gesture (pointer up).
 */
export function handleColumnResizeEndGesture<C extends string>(
  root: HTMLDivElement,
  tableId: string,
  event: PointerEvent,
  appliedColumnWidths: ColumnWidths<C>,
  resizeInfoRef: MutableRef<ColumnResizeInfo<C>>,
  isRtl: boolean,
  onColumnResize?: (detail: ColumnResizeDetail<C>) => void
) {
  if (resizeInfoRef.current.isResizing) {
    if (onColumnResize != null) {
      const resizeColumnWidths = [];
      for (const columnWidth of resizeInfoRef.current.fullColumnWidths) {
        resizeColumnWidths.push(columnWidth);
      }
      const innerTable = root.querySelector(`[data-oj-table-inner-table='${tableId}']`);
      if (innerTable != null) {
        const rect = innerTable.getBoundingClientRect();
        const currentX = isRtl ? rect.right - event.clientX : event.clientX - rect.left;

        resizeInfoRef.current.delta = currentX - resizeInfoRef.current.startX;
        onColumnResize({
          key: resizeInfoRef.current.key,
          delta: resizeInfoRef.current.delta,
          allColumnWidths: appliedColumnWidths
        });
      }
    }
  }
  resizeInfoRef.current = {
    isResizing: false,
    isResizeHover: resizeInfoRef.current.isResizeHover,
    ignoreClick: resizeInfoRef.current.ignoreClick
  };
}

/**
 * Helper function to handle the pointer leaving the entire Table when previously hovering
 * over a column resize region.
 */
export function handleLeaveResizingGesture<C>(
  root: HTMLDivElement,
  tableId: string,
  resizeInfoRef: MutableRef<ColumnResizeInfo<C>>
) {
  if (!resizeInfoRef.current.isResizing) {
    const headerRowElement = root.querySelector(
      `[data-oj-table-header-row='${tableId}']`
    ) as HTMLElement;
    if (headerRowElement != null) {
      headerRowElement.style.cursor = '';
    }
    resizeInfoRef.current.isResizeHover = false;
  }
}

/**
 * Helper function to handle a pointer entering the Table when it was previously handling
 * a column resize gesture.
 */
export function handleEnterResizingGesture<C extends string>(
  root: HTMLDivElement,
  tableId: string,
  event: PointerEvent,
  appliedColumnWidths: ColumnWidths<C>,
  resizeInfoRef: MutableRef<ColumnResizeInfo<C>>,
  isRtl: boolean,
  onColumnResize?: (detail: ColumnResizeDetail<C>) => void
) {
  // handle case where user was resizing, moved pointer out of the Table,
  // released the button, and has now entered the table once again
  if (resizeInfoRef.current.isResizing && event.buttons === 0) {
    handleColumnResizeEndGesture(
      root,
      tableId,
      event,
      appliedColumnWidths,
      resizeInfoRef,
      isRtl,
      onColumnResize
    );
  }
}
